import { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Stack,
  Typography,
} from '@mui/material';
import { AutoAwesome as SparkleIcon } from '@mui/icons-material';
import apiClient from '@/api/client';
import { TYPE } from '@/theme/typography';

/**
 * Staff-portal "Explain this payslip" panel. Click reveals a plain-English
 * walkthrough of gross → deductions → net, with a delta reference vs the
 * prior period when available. Rule-first: the backend assembles the
 * numeric breakdown deterministically; Claude only rewrites it as prose.
 * When Claude isn't configured (dev / quota out), the deterministic
 * breakdown ships as-is so the button always produces something useful.
 */

interface Props {
  lineId: string;
}

export default function PayslipExplainPanel({ lineId }: Props) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ explanation: string; is_ai: boolean } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchExplanation = async () => {
    // First-open lazy fetch — an employee may open/close the drawer multiple
    // times; cache the first answer rather than re-burning Haiku tokens.
    if (result) {
      setOpen(true);
      return;
    }
    setOpen(true);
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get<{ explanation: string; is_ai: boolean }>(
        `/payroll/payslips/${lineId}/explain`,
      );
      setResult(res.data);
    } catch {
      setError("Couldn't load the explanation right now. Try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      {!open ? (
        <Button
          variant="outlined"
          size="small"
          startIcon={<SparkleIcon fontSize="small" />}
          onClick={fetchExplanation}
        >
          Explain this payslip
        </Button>
      ) : (
        <Collapse in={open}>
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
              <Typography sx={{ fontSize: TYPE.sm, fontWeight: TYPE.semibold }}>
                Explanation
              </Typography>
              {result?.is_ai && (
                <Chip label="AI-generated" size="small" icon={<SparkleIcon fontSize="small" />} />
              )}
            </Stack>
            {loading && (
              <Stack direction="row" alignItems="center" gap={1}>
                <CircularProgress size={14} />
                <Typography sx={{ fontSize: TYPE.xs, color: 'text.secondary' }}>
                  Reading your payslip…
                </Typography>
              </Stack>
            )}
            {error && (
              <Typography sx={{ fontSize: TYPE.sm, color: 'error.main' }}>{error}</Typography>
            )}
            {result && (
              <Typography
                sx={{
                  fontSize: TYPE.sm,
                  color: 'text.primary',
                  whiteSpace: 'pre-wrap',
                  lineHeight: 1.6,
                }}
              >
                {result.explanation}
              </Typography>
            )}
            <Button
              size="small"
              onClick={() => setOpen(false)}
              sx={{ mt: 1.5, textTransform: 'none', fontSize: TYPE.xs }}
            >
              Close
            </Button>
          </Box>
        </Collapse>
      )}
    </Box>
  );
}
